import * as Application from 'expo-application'
import * as Updates from 'expo-updates'
import { Platform } from 'react-native'

import { EnvTypes, NFT_CDN_URL, RELAYER_URL, SENTRY_DSN, VELCRO_URL } from '@env'

import {
  LibertyApiEnv,
  setGaslessApiBase,
  setLibertyApiBase,
  setLibertyApiEnv,
  setLibertyAppApiBase,
  setLibertyDexApiBase
} from '@ambire-common/services/libertySwap/apiBase'
import { setGaslessV2Contract } from '@ambire-common/services/libertySwap/gasless'
import { setZkxIntegratorAddress } from '@ambire-common/services/libertySwap/integrator'
import { setZkxTradeApiBase } from '@ambire-common/services/zkxTrade/apiBase'
import { setZkxTradeEnvTargets } from '@ambire-common/services/zkxTrade/planGuard'
import { byZkxEnv, isZkxDevEnv, isZkxProdEnv, ZKX_ENV } from '@ambire-common/utils/zkxEnv'

import appJSON from '../../../../app.json'

export const isTesting = process.env.IS_TESTING === 'true'
export const isDev = process.env.APP_ENV === 'development'
export const isProd = process.env.APP_ENV === 'production'
export const isStaging = process.env.APP_ENV === 'staging'
export const isBenzin = process.env.BENZIN === 'true'
export const isLegends = process.env.LEGENDS === 'true'
/**
 * Ambire Next is a separate production build variant used for beta testing and preview
 * before releasing features to the main production build. It allows us to have two
 * production webkit builds with different branding for testing purposes.
 */
export const isAmbireNext = process.env.AMBIRE_NEXT === 'true'

/**
 * The deployment this build talks to — see `ENV_MODE` in `.env` and the resolver in
 * `@ambire-common/utils/zkxEnv`. Re-exported here because this module is where
 * the rest of the app looks for configuration.
 */
export const ZKX_ENVIRONMENT = ZKX_ENV

/**
 * The mobile-web build pointed at the dev backend. Kept as a named export
 * because a few dev-only surfaces gate on "mobile-web AND dev", not on dev alone.
 */
export const isMobileWebDevBuild = process.env.MOBILE_WEBAPP === 'true' && isZkxDevEnv

/**
 * The mobile-web build on a NON-PRODUCTION backend — dev or uat. Only for the
 * relaxed testing floors on amount inputs (shield/unshield minimums), never for
 * hosts, contracts or anything a user's funds depend on.
 *
 * Separate from `isMobileWebDevBuild` on purpose: a const named `...DevBuild`
 * that is also true on uat lies to the next consumer, and this module is where
 * the rest of the app looks for configuration. Widening the flag itself would
 * have been invisible today (it has exactly one caller) and wrong the day a
 * second one arrives.
 *
 * uat counting as "not prod" for a minimum is the existing decision, not a new
 * one: `SWAP_MIN_USD` in the swapAndBridge controller is already
 * `isZkxProdEnv ? 10 : 0.01`, and the header of `@ambire-common/utils/zkxEnv`
 * records that this is the one thing uat relaxes.
 */
export const isRelaxedAmountLimitsBuild = process.env.MOBILE_WEBAPP === 'true' && !isZkxProdEnv

// LibertySwap is the other backend that has to follow the same switch: its
// router registry, quote pricing, gasless relayer, relayer contract and
// integrator id must all come from one environment (calldata built for the dev
// contracts reverts on prod routers). The swap service is env-agnostic by
// submodule rule, so the value is pushed in from here. The HOSTS come from the
// keys below rather than from `ENV_MODE`; what `ENV_MODE` still selects here is the
// gasless relayer contract and the integrator id (and the host fallback for a
// build that sets neither).
export const libertyApiEnv: LibertyApiEnv = ZKX_ENV

setLibertyApiEnv(libertyApiEnv)

// All four LibertySwap bases come from `.env` — LibertySwap versions and moves
// them independently of anything ZKX owns (production went to
// apis.libertyswap.finance/v3 on 2026-08-19), so they must be changeable without
// a submodule bump. A key left empty falls back to the submodule's table for
// `libertyApiEnv`, which is what keeps targets that never swap (benzin,
// legends, CI) building without carrying swap config.
setLibertyApiBase(process.env.LBS_CCTP_API_URL)
setLibertyAppApiBase(process.env.LBS_API_URL)
setGaslessApiBase(process.env.GASLESS_API_URL)
setLibertyDexApiBase(process.env.LBS_DEX_API_URL)

// ZKX Trade (the same-chain swap aggregator) is our own backend, so unlike the
// LibertySwap bases it has a built-in default and this key is only needed to
// point a build at a staging/local deployment.
//
// The base carries no version segment: the gateway adds `/v1` itself, so a base
// ending in `/v1` turns every request into `/trade/v1/v1/routes` and a 404
// (Trello #571 — the dev deployment's CI variable had it). Drop it here rather
// than rely on every environment's variables being right.
const stripTradeApiVersion = (url: string | undefined) =>
  (url || '').trim().replace(/\/+$/, '').replace(/\/v1$/i, '')
setZkxTradeApiBase(stripTradeApiVersion(process.env.ZKX_TRADE_API_URL))

// Extra PulseChain routers the swap guard trusts for ZKX Trade routes, on top of
// the ones pinned in `planGuard.ts` (comma-separated). Every environment's
// forwarder is pinned there, so these keys only cover a redeploy the submodule
// has not caught up with. An aggregator with NO trusted router is dropped at
// quote time; one whose backend moved to an unknown router is offered and then
// refused on Proceed ("Swap blocked: unverified transaction").
const parseAddressList = (raw: string | undefined) =>
  (raw || '')
    .split(',')
    .map((address) => address.trim())
    .filter(Boolean)
setZkxTradeEnvTargets(369, 'libertyx', parseAddressList(process.env.LIBERTYX_FORWARDER_ADDRESS))
setZkxTradeEnvTargets(369, 'switch', parseAddressList(process.env.SWITCH_ROUTER_ADDRESSES))

// The integrator id ends up inside signed payloads AND has to be registered in
// LibertySwap's on-chain IntegratorRegistry; when they rotate one, every quote
// starts failing with `400 Integrator ... is not registered`. This key unbreaks
// swapping from `.env` instead of a submodule bump. Empty/unset = use the table.
setZkxIntegratorAddress(process.env.NEXT_PUBLIC_CCTP_DEFAULT_INTEGRATOR)

// The LBSGasless v2 contract is the EIP-3009 `to`, so it is covered by the
// user's signature while the relayer submits with its OWN — they must be the
// same address or the token rejects the authorization AFTER the user has signed.
// Both environments' addresses are built into GASLESS_V2_RELAYER_CONTRACTS, so
// this is only an escape hatch for pointing a build somewhere else; it is NOT
// required for gasless to work.
//
// It is honoured on every environment now, not just dev: the same key is how a
// production deployment gets pointed at a redeployed relayer contract without
// waiting for a submodule bump. The table remains the source of truth when the
// key is empty, which is the normal case.
//
// (Previously this was gated on "is this the dev backend" so a stray .env entry
// could not redirect what users sign against on a release. That protection moved to
// the value itself: `.env` now ships the production address explicitly, and any
// override is visible in the same file as the environment it belongs to.)
setGaslessV2Contract(process.env.NEXT_PUBLIC_GASLESS_V2_CONTRACT)

export const ZKX_API_PROD_HOST = 'https://apis.zkxwallet.com'
export const ZKX_API_DEV_HOST = 'https://apis.zkxteam.xyz'
/**
 * Backend host for ZKX-owned APIs (relayer/velcro/cena/rail-squid/invictus).
 * `uat` is a release candidate on the production backend, so it resolves to the
 * production host — spelled out rather than folded into an `isDev ? :` branch.
 * The submodule keeps its own copy of this table (`ZKX_API_BASE` in
 * `@ambire-common/utils/networks`, which it cannot import from here); the two
 * must be changed together.
 */
export const ZKX_API_BASE = byZkxEnv({
  dev: ZKX_API_DEV_HOST,
  uat: ZKX_API_PROD_HOST,
  prod: ZKX_API_PROD_HOST
})
/**
 * The ZKX relayer root, per deployment:
 *
 *   dev       https://apis.zkxteam.xyz/relayer/api
 *   uat/prod  https://apis.zkxwallet.com/relayer/api
 *
 * `/relayer` (without `/api`) is a DIFFERENT backend on both hosts — the legacy
 * Ambire proxy. It answers, which is what makes this dangerous: it serves
 * `/v2/config/networks` and `/gas-tank/assets` with a plausible but
 * Ambire-shaped payload (40 Ambire chains, no PulseChain, Ambire's gas-tank
 * balance) and fails `/v2/get-txn-id` and `/v2/identity/create-multiple`
 * outright with "Ambire relayer error". A build pointed there looks healthy and
 * is silently talking to the wrong relayer.
 *
 * Until 2026-10 the dev host still had ZKX on `/relayer` and 404'd `/api`, so
 * dev mapped to `/relayer`. The dev host now has the prod layout, and that
 * mapping left every dev build (staging web app, TestFlight) on the Ambire
 * proxy: chain 369 lost `hasRelayer`, so Smart Accounts on PulseChain could
 * neither top up nor pay with the Gas Tank (Trello #537). Re-probed 2026-10-21.
 *
 * `/cena` and `/invictus` live outside `/relayer` and keep the plain host swap
 * below.
 */
const ZKX_RELAYER_PROD_ROOT = `${ZKX_API_PROD_HOST}/relayer/api`
const ZKX_RELAYER_DEV_ROOT = `${ZKX_API_DEV_HOST}/relayer/api`
const ZKX_RELAYER_DEV_LEGACY_ROOT = `${ZKX_API_DEV_HOST}/relayer`
const ZKX_RELAYER_ROOT = byZkxEnv({
  dev: ZKX_RELAYER_DEV_ROOT,
  uat: ZKX_RELAYER_PROD_ROOT,
  prod: ZKX_RELAYER_PROD_ROOT
})

/**
 * Repoint a hardcoded production ZKX URL at the current environment's
 * equivalent. A no-op on uat/prod, where the `.env` values are already right.
 *
 * A `.env` carrying the old dev root (`apis.zkxteam.xyz/relayer`, no `/api` —
 * the staging CI `ENV` variable still does) is moved onto the dev ZKX root, so
 * it cannot land on the Ambire proxy. The same mistake on the prod host is NOT
 * rewritten (benzin points there on purpose): keep `.env` on `/relayer/api`.
 */
export const toZkxApiHost = (url: string): string => {
  if (url.startsWith(ZKX_RELAYER_PROD_ROOT))
    return ZKX_RELAYER_ROOT + url.slice(ZKX_RELAYER_PROD_ROOT.length)

  const swapped =
    ZKX_API_BASE === ZKX_API_PROD_HOST ? url : url.replace(ZKX_API_PROD_HOST, ZKX_API_BASE)
  const rest = swapped.slice(ZKX_RELAYER_DEV_LEGACY_ROOT.length)
  if (
    swapped.startsWith(ZKX_RELAYER_DEV_LEGACY_ROOT) &&
    !swapped.startsWith(ZKX_RELAYER_DEV_ROOT) &&
    /^($|[/?])/.test(rest)
  )
    return ZKX_RELAYER_DEV_ROOT + rest

  return swapped
}

/** On Android, this is the package name. On iOS, this is the bundle ID. */
export const APP_ID = Application.applicationId
/**
 * Internal app version, example: 1.0.0 (follows semantic versioning).
 * Fallback to the appJSON version, because in web mode Constants are missing.
 */
export const APP_VERSION = appJSON.version
/**
 * The internal build version of the native build (binary).
 * This is the Info.plist value for `CFBundleVersion` on iOS and
 * the `versionCode` set by `build.gradle` on Android.
 */
export const BUILD_NUMBER = Application.nativeBuildVersion || 'N/A'

export const RELEASE_CHANNEL = Updates.channel || 'N/A'
export const RUNTIME_VERSION = Updates.runtimeVersion || 'N/A'
// FIXME: We should figure out a way to get this,
// because 'expo-constants' uses window refs that break our extension service worker
// export const EXPO_SDK = Constants?.manifest?.sdkVersion || 'N/A'
export const EXPO_SDK = 'N/A'

export const isiOS = Platform.OS === 'ios'
export const isAndroid = Platform.OS === 'android'
export const isMobile = Platform.OS === 'ios' || Platform.OS === 'android'
export const isWeb = Platform.OS === 'web'
export const isMobileWebApp =
  Platform.OS === 'web' &&
  typeof window !== 'undefined' &&
  window.location?.protocol !== 'chrome-extension:'
/**
 * The mobile-web build (`isMobileWebApp`) running inside an actual mobile-device
 * browser (iOS/Android), as opposed to a desktop browser. Detected via userAgent
 * because in react-native-web every browser reports `Platform.OS === 'web'`.
 * iPadOS 13+ masquerades as macOS, so we additionally treat a touch-capable
 * "Macintosh" as mobile. Used to shrink private-mode balance scanning to a single
 * chain on resource-constrained mobile browsers (avoids OOM crashes).
 */
export const isMobileBrowser =
  isMobileWebApp &&
  typeof navigator !== 'undefined' &&
  (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '') ||
    (/Macintosh/.test(navigator.userAgent || '') && (navigator as any).maxTouchPoints > 1))

/**
 * The concrete OS of the mobile browser running the mobile-web build, derived
 * from the userAgent (Platform.OS is always 'web' here, so `isiOS`/`isAndroid`
 * are useless on this surface). Returns null on desktop browsers / native.
 * iPadOS 13+ masquerades as "Macintosh", so a touch-capable Mac is treated as
 * iOS. Used to show the OS-specific "Add to Home Screen" install instructions.
 */
export const mobileBrowserOS: 'ios' | 'android' | null = (() => {
  if (!isMobileWebApp || typeof navigator === 'undefined') return null
  const ua = navigator.userAgent || ''
  if (
    /iPhone|iPad|iPod/i.test(ua) ||
    (/Macintosh/.test(ua) && (navigator as any).maxTouchPoints > 1)
  )
    return 'ios'
  if (/Android/i.test(ua)) return 'android'
  return null
})()

// eslint-disable-next-line @typescript-eslint/naming-convention
enum APP_ENV {
  PROD = 'production',
  STAGING = 'staging',
  DEV = 'development'
}

interface Config extends EnvTypes {
  APP_ENV: APP_ENV
}

const CONFIG: Config = {
  APP_ENV: APP_ENV.DEV,
  // On a dev build these point at apis.zkxteam.xyz; on every other build
  // `toZkxApiHost` is a no-op and the .env values pass through.
  RELAYER_URL: toZkxApiHost(RELAYER_URL),
  VELCRO_URL: toZkxApiHost(VELCRO_URL),
  SENTRY_DSN,
  NFT_CDN_URL: NFT_CDN_URL || 'https://nftcdn.ambire.com',
  ENVIRONMENT: process.env.ENVIRONMENT || 'development',
  DEFAULT_KEYSTORE_PASSWORD_DEV: process.env.DEFAULT_KEYSTORE_PASSWORD_DEV || '',
  LEGENDS_NFT_ADDRESS:
    process.env.LEGENDS_NFT_ADDRESS || '0xF51dF52d0a9BEeB7b6E4B6451e729108a115B863',
  SENTRY_DSN_LEGENDS: process.env.SENTRY_DSN_LEGENDS || '',
  SENTRY_DSN_BROWSER_EXTENSION: process.env.SENTRY_DSN_BROWSER_EXTENSION || '',
  BUNGEE_API_KEY: process.env.BUNGEE_API_KEY || '',
  LI_FI_API_KEY: process.env.LI_FI_API_KEY || ''
}

if (isProd) {
  CONFIG.APP_ENV = APP_ENV.PROD
} else if (isStaging) {
  CONFIG.APP_ENV = APP_ENV.STAGING
}

/**
 * Option to run the app without the ZKX Relayer. See `RELAYER_URL`
 * That's special app mode, which is meant to be turned on only manually.
 * TODO: This is never tested, so it might not work!
 */
export const isRelayerless = !CONFIG.RELAYER_URL

export default CONFIG
